import { Alert, Button, Col, Popconfirm, Radio, Row, Typography } from 'antd'
import Container from 'components/Container'
import { useLocalization } from 'localization'
import { useEffect, useMemo, useState } from 'react'
import { useChangePlan, useCheckout, useCreditPackages, useCurrentUser, usePlans, useStripeDashboardLink } from 'api'
import Styled from './styled'
import { useTheme } from 'styled-components'
import { useOrganizationById } from 'api/organizations/queries'
import appRoutes from 'modules/common/app-routes'
import { PlanSkeleton } from './skeleton'
import { PlanType } from 'api/billings/dtos'

const returnUrl = typeof window !== 'undefined' ? `${window.location.origin}${appRoutes.dashboard()}` : undefined

const BillingTab = () => {
	const theme = useTheme()
	const { data: user, clientId, isFetched: isUserFetched } = useCurrentUser()
	const { data: organization } = useOrganizationById(user?.orgId)
	const { data: traderPlans = [], isFetched: arePlansFetched } = usePlans(user?.jurisdiction, {
		enabled: isUserFetched && !!user?.jurisdiction && !user.isCPA,
	})
	const { data: creditPacks = [], isFetched: arePacksFetched } = useCreditPackages(user?.jurisdiction, {
		enabled: isUserFetched && !!user?.jurisdiction && user.isCPA && !!organization?.isAdmin,
	})
	const [selectedPackId, selectPackId] = useState<string | undefined>()
	const [selectedPlan, selectPlan] = useState<PlanType | undefined>()
	const { t, f } = useLocalization()
	const { mutateAsync: checkout } = useCheckout()
	const { data: stripeDashboardLink } = useStripeDashboardLink(returnUrl, {
		enabled: !!returnUrl && isUserFetched && user?.hasCustomerId,
	})
	const { mutateAsync: changePlan, isLoading: isPlanChanging } = useChangePlan()

	const showNextPayment = useMemo(
		() =>
			isUserFetched && user?.currentPlan !== 'FREE' && user?.paidUntil && user.paidUntil && new Date() < user.paidUntil,
		[isUserFetched, user]
	)
	const showPaidUntil = useMemo(
		() => isUserFetched && user?.currentPlan === 'FREE' && user.paidUntil && new Date() < user.paidUntil,
		[user, isUserFetched]
	)

	useEffect(() => {
		if (user?.currentPlan) selectPlan(user.currentPlan)
	}, [user?.currentPlan])

	const onSubmit = async () => {
		if (selectedPlan === 'CUSTOM') {
			window.location.href = 'mailto:contact@blocksentry.co'
			return
		}
		if (selectedPlan === 'FREE') {
			await changePlan(selectedPlan)
			return
		}
		const foundProductId = traderPlans.find(({ type }) => selectedPlan === type)
		if (foundProductId?.priceId) {
			let successQueryString = ''

			if (foundProductId.type !== 'FREE') {
				successQueryString = '?type=premium'
			}

			await checkout({
				priceId: foundProductId.priceId,
				successUrl: `${window.location.origin}${appRoutes.billings.success()}${successQueryString}`,
				cancelUrl: `${window.location.origin}${appRoutes.billings.cancel()}`,
			})
		}
	}

	const onBuyCredits = async () => {
		if (selectedPackId) {
			await checkout({
				priceId: selectedPackId,
				successUrl: `${window.location.origin}${appRoutes.billings.success()}?type=credits`,
				cancelUrl: `${window.location.origin}${appRoutes.billings.cancel()}`,
			})
		}
	}

	return (
		<Container display="flex" flexDirection="column" space="md" spaceDirection="vertical">
			{showNextPayment && user?.paidUntil && (
				<Alert
					type="info"
					message={t('billings.nextPayment', { date: f(user.paidUntil, { date: 'medium', time: 'medium' }) })}
				/>
			)}
			{showPaidUntil && user?.paidUntil && (
				<Alert type="info" message={t('billings.paidUntil', { date: f(user.paidUntil, { date: 'medium' }) })} />
			)}
			{user?.isCPA && organization?.isAdmin && !clientId && arePacksFetched && (
				<Container display="flex" flexDirection="column" space="sm" spaceDirection="vertical">
					<Typography.Title level={5}>{t('billings.creditPacksTitle')}</Typography.Title>
					<Radio.Group onChange={e => selectPackId(e.target.value)} value={selectedPackId}>
						<Container display="flex" flexDirection="column" space="sm" spaceDirection="vertical">
							{creditPacks.map(plan => (
								<Styled.PackCard
									key={plan.name}
									onClick={() => selectPackId(plan.priceId)}
									$selected={plan.priceId === selectedPackId}
								>
									<Radio value={plan.priceId} />
									<Row gutter={[theme.margins.sm, theme.margins.sm]}>
										<Col xs={24} sm={6}>
											<Typography.Title level={5}>{t(`billings.creditPacks.${plan.name}`)}</Typography.Title>
										</Col>
										<Col xs={16} sm={13}>
											<Typography.Title level={5}>
												{t('terms.credits')}: {plan.credits}
											</Typography.Title>
										</Col>
										<Col xs={8} sm={5}>
											<Typography.Title level={5}>
												{f(plan.amount, { currency: plan.currency.toUpperCase() })}
											</Typography.Title>
										</Col>
									</Row>
								</Styled.PackCard>
							))}
						</Container>
					</Radio.Group>
				</Container>
			)}
			{!user?.isCPA && traderPlans.length > 0 && arePlansFetched && (
				<Container display="flex" flexDirection="column" space="sm" spaceDirection="vertical">
					<Typography.Title level={5}>{t('settings.billing.plan')}</Typography.Title>
					<Radio.Group onChange={e => selectPlan(e.target.value)} value={selectedPlan}>
						<Container display="flex" flexDirection="column" space="sm" spaceDirection="vertical">
							{traderPlans.map(plan => (
								<Styled.PlanCard
									key={plan.name}
									onClick={() => selectPlan(plan.type)}
									$selected={plan.type === selectedPlan}
								>
									<Radio value={plan.type} />
									<Row gutter={[theme.margins.sm, theme.margins.sm]}>
										<Col xs={24} sm={5}>
											<Typography.Title level={5}>{plan.name}</Typography.Title>
											{plan.type === user?.currentPlan && <Typography.Text>({t('terms.current')})</Typography.Text>}
										</Col>
										<Col xs={24} sm={13}>
											<Container display="flex" flexDirection="column">
												<Styled.PlanDescription>{plan.description}</Styled.PlanDescription>
											</Container>
										</Col>
										<Col xs={24} sm={6}>
											<Typography.Title level={5}>{plan.price}</Typography.Title>
										</Col>
									</Row>
								</Styled.PlanCard>
							))}
						</Container>
					</Radio.Group>
				</Container>
			)}
			{!user?.isCPA && traderPlans.length === 0 && arePlansFetched && (
				<Container display="flex" justifyContent="center" alignItems="center">
					<Typography.Text>{t('settings.billing.emptyPlans')}</Typography.Text>
				</Container>
			)}
			{!user?.isCPA && !arePlansFetched && <PlanSkeleton />}
			{user?.isCPA && organization?.isAdmin && !arePacksFetched && <PlanSkeleton />}
			{stripeDashboardLink && (
				<Container display="flex" flexDirection="column" space="sm" spaceDirection="vertical">
					<Typography.Link target="_blank" href={stripeDashboardLink}>
						{t('settings.billing.historyAndSubscription')}
					</Typography.Link>
				</Container>
			)}
			{!user?.isCPA && traderPlans.length > 0 && (
				<Container>
					{selectedPlan === 'FREE' ? (
						<Popconfirm
							title={`${t('billings.changePlan.freeChaneWarning', {
								plan: t(`billings.userPlans.${selectedPlan}.name`),
							})}${
								user?.paidUntil
									? t('billings.changePlan.untilDate', { date: f(user.paidUntil, { date: 'medium', time: 'medium' }) })
									: ''
							}`}
							onConfirm={() => onSubmit()}
							okText={t('actions.confirm')}
							cancelText={t('actions.cancel')}
							okButtonProps={{
								loading: isPlanChanging,
							}}
							icon={null}
						>
							<Button type="primary" disabled={!selectedPlan || selectedPlan === user?.currentPlan}>
								{t('actions.changePlan')}
							</Button>
						</Popconfirm>
					) : (
						<Button type="primary" disabled={!selectedPlan || selectedPlan === user?.currentPlan} onClick={onSubmit}>
							{t('actions.changePlan')}
						</Button>
					)}
				</Container>
			)}
			{user?.isCPA && organization?.isAdmin && (
				<Container>
					<Button type="primary" disabled={!selectedPackId || !arePacksFetched} onClick={onBuyCredits}>
						{t('actions.buyCredits')}
					</Button>
				</Container>
			)}
		</Container>
	)
}

export default BillingTab
